import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, Monitor } from "lucide-react";
import * as api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "../ui/dialog";
import { EmptyState } from "../shared/EmptyState";
import { PageLoader } from "../shared/LoadingSpinner";
import { toast } from "sonner";

export function AdminEquipment() {
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        equipment_type: "",
        description: "",
    });

    useEffect(() => {
        loadEquipment();
    }, []);

    const loadEquipment = async () => {
        try {
            setLoading(true);
            const data = await api.getEquipmentCatalog();
            setEquipment(data);
        } catch (error) {
            toast.error("Erreur lors du chargement");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditing(item);
            setFormData({
                name: item.name,
                equipment_type: item.equipment_type,
                description: item.description || "",
            });
        } else {
            setEditing(null);
            setFormData({ name: "", equipment_type: "", description: "" });
        }
        setDialogOpen(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await api.updateEquipment(editing.id, formData);
                toast.success("Matériel mis à jour");
            } else {
                await api.createEquipment(formData);
                toast.success("Matériel créé");
            }
            setDialogOpen(false);
            loadEquipment();
        } catch (error) {
            toast.error("Erreur lors de la sauvegarde");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce matériel ?")) return;
        try {
            await api.deleteEquipment(id);
            toast.success("Matériel supprimé");
            loadEquipment();
        } catch (error) {
            toast.error("Erreur lors de la suppression");
        }
    };

    if (loading) return <PageLoader />;

    return (
        <div className="page-container" data-testid="admin-equipment">
            <div className="page-header flex items-center justify-between">
                <div>
                    <h1 className="page-title">Catalogue matériel</h1>
                    <p className="page-subtitle">{equipment.length} types de matériel</p>
                </div>
                <Button onClick={() => handleOpenDialog()} data-testid="add-equipment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                </Button>
            </div>

            {equipment.length === 0 ? (
                <div className="card">
                    <EmptyState
                        icon={Monitor}
                        title="Aucun matériel"
                        description="Ajoutez des types de matériel au catalogue"
                    />
                </div>
            ) : (
                <div className="card">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Type</th>
                                    <th>Description</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {equipment.map((eq) => (
                                    <tr key={eq.id} data-testid={`equipment-row-${eq.id}`}>
                                        <td className="font-medium">{eq.name}</td>
                                        <td>{eq.equipment_type}</td>
                                        <td className="text-gray-500">{eq.description || "—"}</td>
                                        <td>
                                            <div className="flex items-center gap-2 justify-end">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenDialog(eq)}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDelete(eq.id)}
                                                >
                                                    <Trash2 className="w-4 h-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing ? "Modifier le matériel" : "Ajouter un matériel"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="form-label">Nom *</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Ordinateur portable"
                                data-testid="equipment-name"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Type *</Label>
                            <Input
                                value={formData.equipment_type}
                                onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                                placeholder="Ex: laptop, monitor, phone"
                                data-testid="equipment-type"
                            />
                        </div>
                        <div>
                            <Label className="form-label">Description</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Description du matériel"
                                data-testid="equipment-description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSave} disabled={!formData.name || !formData.equipment_type} data-testid="save-equipment-btn">
                            {editing ? "Enregistrer" : "Créer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
